import { defineMessage, defineMessages, IntlShape } from 'react-intl';
import { XpTypeMap } from '../types/quiz';

export const EstimatedTimePerQuestion = {
  FLASHCARD: 0.21,
  QUESTION: 0.83,
  NA: 0.52, // Average between the two above
};

export type QuizTypeGroup = keyof typeof EstimatedTimePerQuestion;

export enum optionStateEnum {
  default,
  chosen,
  correct,
  incorrect,
  eliminated,
}

export const xpTypeValues = {
  [XpTypeMap.CORRECT]: 3,
  [XpTypeMap.EXTRA]: 1,
};

export const xpTypeTextMap = defineMessages({
  [XpTypeMap.CORRECT]: {
    id: 'common.correctAnswers',
    defaultMessage: 'Correct answers',
  },
  [XpTypeMap.EXTRA]: {
    id: 'common.explanationsReviewed',
    defaultMessage: 'Explanations reviewed',
  },
  total: {
    id: 'common.total',
    defaultMessage: 'Total',
  },
});

/**
 *Get style from current state.
 */
export const getOptionColor = (value: optionStateEnum) => {
  switch (value) {
    case optionStateEnum.correct:
      return {
        color: 'gray60',
        bg: 'positiveLight',
        borderColor: 'positive',
        textDecoration: 'none',
      };
    case optionStateEnum.incorrect:
      return {
        color: 'gray60',
        bg: 'negativeLight',
        borderColor: 'negative',
        textDecoration: 'none',
      };
    case optionStateEnum.eliminated:
      return {
        color: 'gray40',
        bg: 'bg-0',
        borderColor: '#7ea4cf',
        textDecoration: 'line-through',
      };
    case optionStateEnum.chosen:
      return {
        color: 'gray60',
        bg: 'bg-0',
        borderColor: 'primary',
        textDecoration: 'none',
      };
    default:
      return {
        color: 'gray60',
        bg: 'bg-0',
        borderColor: 'gray10',
        textDecoration: 'none',
      };
  }
};

export const MIN_ITEMS_FOR_JOYSNACKBAR = 10;

export const TABLE_OPTION = defineMessages({
  blood: { id: 'quiz.table.option.blood', defaultMessage: 'Blood' },
  hematologic: {
    id: 'quiz.table.option.hematologic',
    defaultMessage: 'Hematologic',
  },
  cerebrospinal: {
    id: 'quiz.table.option.cerebrospinal',
    defaultMessage: 'Cerebrospinal',
  },
  sweat: { id: 'quiz.table.option.sweat', defaultMessage: 'Sweat, Urine, BMI' },
});

export const LAB_VALUES_TABLE = {
  blood: {
    headers: [
      defineMessage({
        id: 'quiz.table.blood.col1.header',
        defaultMessage: 'Blood, Plasma, Serum',
      }),
      defineMessage({
        id: 'quiz.table.blood.col2.header',
        defaultMessage: 'Reference Range',
      }),
      defineMessage({
        id: 'quiz.table.blood.col3.header',
        defaultMessage: 'SI Reference Interval',
      }),
    ],
    rows: [
      [
        defineMessage({
          id: 'quiz.table.blood.row1.name',
          defaultMessage: 'Alanine aminotransferase (ALT), serum',
        }),
        defineMessage({
          id: 'quiz.table.blood.row1.range',
          defaultMessage: '8-40 U/L',
        }),
        defineMessage({
          id: 'quiz.table.blood.row1.interval',
          defaultMessage: '8-40 U/L',
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.blood.row2.name',
          defaultMessage: 'Alkaline phosphatase, serum (p-NPP at 30°C)',
        }),
        defineMessage({
          id: 'quiz.table.blood.row2.range',
          defaultMessage: '20-70 U/L',
        }),
        defineMessage({
          id: 'quiz.table.blood.row2.interval',
          defaultMessage: '20-70 U/L',
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.blood.row3.name',
          defaultMessage: 'Amylase, serum',
        }),
        defineMessage({
          id: 'quiz.table.blood.row3.range',
          defaultMessage: '25-125 U/L',
        }),
        defineMessage({
          id: 'quiz.table.blood.row3.interval',
          defaultMessage: '25-125 U/L',
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.blood.row4.name',
          defaultMessage: 'Aspartate aminotransferase (AST), serum',
        }),
        defineMessage({
          id: 'quiz.table.blood.row4.range',
          defaultMessage: '8-40 U/L',
        }),
        defineMessage({
          id: 'quiz.table.blood.row4.interval',
          defaultMessage: '8-40 U/L',
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.blood.row5.subheader',
          defaultMessage: 'Bilirubin, serum (adult)',
          isSubheader: true,
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.blood.row6.name',
          defaultMessage: 'Total',
        }),
        defineMessage({
          id: 'quiz.table.blood.row6.range',
          defaultMessage: '0.1-1.0 mg/dL',
        }),
        defineMessage({
          id: 'quiz.table.blood.row6.interval',
          defaultMessage: '2-17 μmol/L',
        }),
        defineMessage({
          id: 'quiz.table.blood.row7.name',
          defaultMessage: 'Direct',
        }),
        defineMessage({
          id: 'quiz.table.blood.row7.range',
          defaultMessage: '0.0-0.3 mg/dL',
        }),
        defineMessage({
          id: 'quiz.table.blood.row7.interval',
          defaultMessage: '0.0-0.05 μmol/L',
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.blood.row8.name',
          defaultMessage: 'Calcium, serum (total)',
        }),
        defineMessage({
          id: 'quiz.table.blood.row8.range',
          defaultMessage: '8.4-10.2 mg/dL',
        }),
        defineMessage({
          id: 'quiz.table.blood.row8.interval',
          defaultMessage: '2.1-2.8 mmol/L',
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.blood.row9.subheader',
          defaultMessage: 'Cholesterol, serum',
          isSubheader: true,
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.blood.row10.name',
          defaultMessage: 'Total',
        }),
        defineMessage({
          id: 'quiz.table.blood.row10.range',
          defaultMessage: '150-240 mg/dL',
        }),
        defineMessage({
          id: 'quiz.table.blood.row10.interval',
          defaultMessage: '3.9-6.2 mmol/L',
        }),
        defineMessage({
          id: 'quiz.table.blood.row11.name',
          defaultMessage: 'HDL',
        }),
        defineMessage({
          id: 'quiz.table.blood.row11.range',
          defaultMessage: '30-70 mg/dL',
        }),
        defineMessage({
          id: 'quiz.table.blood.row11.interval',
          defaultMessage: '0.8-1.8 mmol/L',
        }),
        defineMessage({
          id: 'quiz.table.blood.row12.name',
          defaultMessage: 'LDL',
        }),
        defineMessage({
          id: 'quiz.table.blood.row12.range',
          defaultMessage: '<160 mg/dL',
        }),
        defineMessage({
          id: 'quiz.table.blood.row12.interval',
          defaultMessage: '<4.2 mmol/L',
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.blood.row13.subheader',
          defaultMessage: 'Cortisol, serum',
          isSubheader: true,
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.blood.row14.name',
          defaultMessage: '0800 h',
        }),
        defineMessage({
          id: 'quiz.table.blood.row14.range',
          defaultMessage: '5-23 μg/dL',
        }),
        defineMessage({
          id: 'quiz.table.blood.row14.interval',
          defaultMessage: '138-635 nmol/L',
        }),
        defineMessage({
          id: 'quiz.table.blood.row15.name',
          defaultMessage: '1600 h',
        }),
        defineMessage({
          id: 'quiz.table.blood.row15.range',
          defaultMessage: '3-15 μg/dL',
        }),
        defineMessage({
          id: 'quiz.table.blood.row15.interval',
          defaultMessage: '82-413 nmol/L',
        }),
        defineMessage({
          id: 'quiz.table.blood.row16.name',
          defaultMessage: '2000 h',
        }),
        defineMessage({
          id: 'quiz.table.blood.row16.range',
          defaultMessage: '≤50% of 0800 h',
        }),
        defineMessage({
          id: 'quiz.table.blood.row16.interval',
          defaultMessage: 'Fraction of 0800 h: ≤0.50',
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.blood.row17.subheader',
          defaultMessage: 'Creatine kinase, serum',
          isSubheader: true,
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.blood.row18.name',
          defaultMessage: 'Male',
        }),
        defineMessage({
          id: 'quiz.table.blood.row18.range',
          defaultMessage: '25-90 U/L',
        }),
        defineMessage({
          id: 'quiz.table.blood.row18.interval',
          defaultMessage: '25-90 U/L',
        }),
        defineMessage({
          id: 'quiz.table.blood.row19.name',
          defaultMessage: 'Female',
        }),
        defineMessage({
          id: 'quiz.table.blood.row19.range',
          defaultMessage: '10-70 U/L',
        }),
        defineMessage({
          id: 'quiz.table.blood.row19.interval',
          defaultMessage: '10-70 U/L',
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.blood.row20.name',
          defaultMessage: 'Creatinine, serum',
        }),
        defineMessage({
          id: 'quiz.table.blood.row20.range',
          defaultMessage: '0.6-1.2 mg/dL',
        }),
        defineMessage({
          id: 'quiz.table.blood.row20.interval',
          defaultMessage: '53-106 μmol/L',
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.blood.row21.subheader',
          defaultMessage: 'Electrolytes, serum',
          isSubheader: true,
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.blood.row22.name',
          defaultMessage: `Sodium (Na⁺)`,
        }),
        defineMessage({
          id: 'quiz.table.blood.row22.range',
          defaultMessage: '136-146 mEq/L',
        }),
        defineMessage({
          id: 'quiz.table.blood.row22.interval',
          defaultMessage: '136-146 mmol/L',
        }),
        defineMessage({
          id: 'quiz.table.blood.row23.name',
          defaultMessage: 'Potassium (K⁺)',
        }),
        defineMessage({
          id: 'quiz.table.blood.row23.range',
          defaultMessage: '3.5-5.0 mEq/L',
        }),
        defineMessage({
          id: 'quiz.table.blood.row23.interval',
          defaultMessage: '95-105 mmol/L',
        }),
        defineMessage({
          id: 'quiz.table.blood.row24.name',
          defaultMessage: 'Chloride (Cl⁻)',
        }),
        defineMessage({
          id: 'quiz.table.blood.row24.range',
          defaultMessage: '95-105 mEq/L',
        }),
        defineMessage({
          id: 'quiz.table.blood.row24.interval',
          defaultMessage: '3.5-5.0 mmol/L',
        }),
        defineMessage({
          id: 'quiz.table.blood.row25.name',
          defaultMessage: 'Bicarbonate (HCO₃⁻)',
        }),
        defineMessage({
          id: 'quiz.table.blood.row25.range',
          defaultMessage: '22-28 mEq/L',
        }),
        defineMessage({
          id: 'quiz.table.blood.row25.interval',
          defaultMessage: '22-28 mmmol/L',
        }),
        defineMessage({
          id: 'quiz.table.blood.row26.name',
          defaultMessage: 'Magnesium (Mg²⁺)',
        }),
        defineMessage({
          id: 'quiz.table.blood.row26.range',
          defaultMessage: '1.5-2.0 mEq/L',
        }),
        defineMessage({
          id: 'quiz.table.blood.row26.interval',
          defaultMessage: '0.75-1.0 mmol/L',
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.blood.row27.subheader',
          defaultMessage: 'Estriol, total, serum (in pregnancy)',
          isSubheader: true,
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.blood.row28.name',
          defaultMessage: '24-28 wks',
        }),
        defineMessage({
          id: 'quiz.table.blood.row28.range',
          defaultMessage: '30-170 ng/mL',
        }),
        defineMessage({
          id: 'quiz.table.blood.row28.interval',
          defaultMessage: '104-590 nmol/L',
        }),
        defineMessage({
          id: 'quiz.table.blood.row29.name',
          defaultMessage: '28-32 wks',
        }),
        defineMessage({
          id: 'quiz.table.blood.row29.range',
          defaultMessage: '40-220 ng/mL',
        }),
        defineMessage({
          id: 'quiz.table.blood.row29.interval',
          defaultMessage: '140-760 nmol/L',
        }),
        defineMessage({
          id: 'quiz.table.blood.row30.name',
          defaultMessage: '32-36 wks',
        }),
        defineMessage({
          id: 'quiz.table.blood.row30.range',
          defaultMessage: '60-280 ng/mL',
        }),
        defineMessage({
          id: 'quiz.table.blood.row30.interval',
          defaultMessage: '208-970 nmol/L',
        }),
        defineMessage({
          id: 'quiz.table.blood.row31.name',
          defaultMessage: '36-40 wks',
        }),
        defineMessage({
          id: 'quiz.table.blood.row31.range',
          defaultMessage: '80-350 ng/mL',
        }),
        defineMessage({
          id: 'quiz.table.blood.row31.interval',
          defaultMessage: '280-1210 nmol/L',
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.blood.row32.subheader',
          defaultMessage: 'Ferritin, serum',
          isSubheader: true,
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.blood.row33.name',
          defaultMessage: 'Male',
        }),
        defineMessage({
          id: 'quiz.table.blood.row33.range',
          defaultMessage: '15-200 ng/mL',
        }),
        defineMessage({
          id: 'quiz.table.blood.row33.interval',
          defaultMessage: '15-200 μg/L',
        }),
        defineMessage({
          id: 'quiz.table.blood.row34.name',
          defaultMessage: 'Female',
        }),
        defineMessage({
          id: 'quiz.table.blood.row34.range',
          defaultMessage: '12-150 ng/mL',
        }),
        defineMessage({
          id: 'quiz.table.blood.row34.interval',
          defaultMessage: '12-150 μg/L',
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.blood.row35.subheader',
          defaultMessage: 'Follicle-stimulating hormone, serum/plasma',
          isSubheader: true,
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.blood.row36.name',
          defaultMessage: 'Male',
        }),
        defineMessage({
          id: 'quiz.table.blood.row36.range',
          defaultMessage: '4-25 mIU/mL',
        }),
        defineMessage({
          id: 'quiz.table.blood.row36.interval',
          defaultMessage: '4-25 U/L',
        }),
        defineMessage({
          id: 'quiz.table.blood.row37.name',
          defaultMessage: 'Female',
        }),
        defineMessage({
          id: 'quiz.table.blood.row37.range',
          defaultMessage: ' ',
        }),
        defineMessage({
          id: 'quiz.table.blood.row37.interval',
          defaultMessage: ' ',
        }),
        defineMessage({
          id: 'quiz.table.blood.row38.name',
          defaultMessage: 'premenopause',
        }),
        defineMessage({
          id: 'quiz.table.blood.row38.range',
          defaultMessage: '4-30 mIU/mL',
        }),
        defineMessage({
          id: 'quiz.table.blood.row38.interval',
          defaultMessage: '4-30 U/L',
        }),
        defineMessage({
          id: 'quiz.table.blood.row39.name',
          defaultMessage: 'midcycle peak',
        }),
        defineMessage({
          id: 'quiz.table.blood.row39.range',
          defaultMessage: '10-90 mIU/mL',
        }),
        defineMessage({
          id: 'quiz.table.blood.row39.interval',
          defaultMessage: '10-90 U/L',
        }),
        defineMessage({
          id: 'quiz.table.blood.row40.name',
          defaultMessage: 'postmenopause',
        }),
        defineMessage({
          id: 'quiz.table.blood.row40.range',
          defaultMessage: '40-250 mIU/mL',
        }),
        defineMessage({
          id: 'quiz.table.blood.row40.interval',
          defaultMessage: '40-250 U/L',
        }),
      ],

      [
        defineMessage({
          id: 'quiz.table.blood.row41.subheader',
          defaultMessage: 'Gases, arterial blood (room air)',
          isSubheader: true,
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.blood.row42.name',
          defaultMessage: 'PO₂',
        }),
        defineMessage({
          id: 'quiz.table.blood.row42.range',
          defaultMessage: '75-105 mm Hg',
        }),
        defineMessage({
          id: 'quiz.table.blood.row42.interval',
          defaultMessage: '10.0-14.0 kPa',
        }),
        defineMessage({
          id: 'quiz.table.blood.row43.name',
          defaultMessage: 'PCO₂',
        }),
        defineMessage({
          id: 'quiz.table.blood.row43.range',
          defaultMessage: '33-45 mm Hg',
        }),
        defineMessage({
          id: 'quiz.table.blood.row43.interval',
          defaultMessage: '4.4-5.9 kPa',
        }),
        defineMessage({
          id: 'quiz.table.blood.row44.name',
          defaultMessage: 'pH',
        }),
        defineMessage({
          id: 'quiz.table.blood.row44.range',
          defaultMessage: '7.35-7.45',
        }),
        defineMessage({
          id: 'quiz.table.blood.row44.interval',
          defaultMessage: '[H⁺] 36-44 nmol/L',
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.blood.row45.subheader',
          defaultMessage: 'Glucose, serum',
          isSubheader: true,
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.blood.row46.name',
          defaultMessage: 'Fasting',
        }),
        defineMessage({
          id: 'quiz.table.blood.row46.range',
          defaultMessage: '70-110 mg/dL',
        }),
        defineMessage({
          id: 'quiz.table.blood.row46.interval',
          defaultMessage: '3.8-6.1 mmol/L',
        }),
        defineMessage({
          id: 'quiz.table.blood.row47.name',
          defaultMessage: '2-h postprandial',
        }),
        defineMessage({
          id: 'quiz.table.blood.row47.range',
          defaultMessage: ': <120 mg/dL',
        }),
        defineMessage({
          id: 'quiz.table.blood.row47.interval',
          defaultMessage: '<6.6 mmol/L',
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.blood.row48.subheader',
          defaultMessage: 'Growth hormone- arginine stimulation',
          isSubheader: true,
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.blood.row49.name',
          defaultMessage: 'Fasting',
        }),
        defineMessage({
          id: 'quiz.table.blood.row49.range',
          defaultMessage: '5 ng/mL',
        }),
        defineMessage({
          id: 'quiz.table.blood.row49.interval',
          defaultMessage: '5 μg/L',
        }),
        defineMessage({
          id: 'quiz.table.blood.row50.name',
          defaultMessage: 'Provocative stimuli',
        }),
        defineMessage({
          id: 'quiz.table.blood.row50.range',
          defaultMessage: '>7 ng/mL',
        }),
        defineMessage({
          id: 'quiz.table.blood.row50.interval',
          defaultMessage: '>7 μg/L',
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.blood.row51.subheader',
          defaultMessage: 'Immunoglobulins, serum',
          isSubheader: true,
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.blood.row52.name',
          defaultMessage: 'IgA',
        }),
        defineMessage({
          id: 'quiz.table.blood.row52.range',
          defaultMessage: '76-390 mg/dL',
        }),
        defineMessage({
          id: 'quiz.table.blood.row52.interval',
          defaultMessage: '0.76-3.90 g/L',
        }),
        defineMessage({
          id: 'quiz.table.blood.row53.name',
          defaultMessage: 'IgE',
        }),
        defineMessage({
          id: 'quiz.table.blood.row53.range',
          defaultMessage: '0-380 IU/ml',
        }),
        defineMessage({
          id: 'quiz.table.blood.row53.interval',
          defaultMessage: '0-380 kIU/L',
        }),
        defineMessage({
          id: 'quiz.table.blood.row54.name',
          defaultMessage: 'IgG',
        }),
        defineMessage({
          id: 'quiz.table.blood.row54.range',
          defaultMessage: '650-1,500 mg/dL',
        }),
        defineMessage({
          id: 'quiz.table.blood.row54.interval',
          defaultMessage: '6.5-15 g/L',
        }),
        defineMessage({
          id: 'quiz.table.blood.row55.name',
          defaultMessage: 'IgM',
        }),
        defineMessage({
          id: 'quiz.table.blood.row55.range',
          defaultMessage: '40-345 mg/dL',
        }),
        defineMessage({
          id: 'quiz.table.blood.row55.interval',
          defaultMessage: '0.4-3.45 g/L',
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.blood.row56.name',
          defaultMessage: 'Iron',
        }),
        defineMessage({
          id: 'quiz.table.blood.row56.range',
          defaultMessage: '50-170 μg/dL',
        }),
        defineMessage({
          id: 'quiz.table.blood.row56.interval',
          defaultMessage: '9-30 μmol/L',
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.blood.row57.name',
          defaultMessage: 'Lactate dehydrogenase, serum',
        }),
        defineMessage({
          id: 'quiz.table.blood.row57.range',
          defaultMessage: '45-90 U/L (100-250 IU/L)',
        }),
        defineMessage({
          id: 'quiz.table.blood.row57.interval',
          defaultMessage: '45-90 U/L (100-250 IU/L)',
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.blood.row58.subheader',
          defaultMessage: 'Luteinizing hormone, serum/plasma',
          isSubheader: true,
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.blood.row59.name',
          defaultMessage: 'Male',
        }),
        defineMessage({
          id: 'quiz.table.blood.row59.range',
          defaultMessage: '6-23 mIU/mL',
        }),
        defineMessage({
          id: 'quiz.table.blood.row59.interval',
          defaultMessage: '6-23 U/L',
        }),
        defineMessage({
          id: 'quiz.table.blood.row60.name',
          defaultMessage: 'Female: follicular phase',
        }),
        defineMessage({
          id: 'quiz.table.blood.row60.range',
          defaultMessage: '5-30 mIU/mL',
        }),
        defineMessage({
          id: 'quiz.table.blood.row60.interval',
          defaultMessage: '5-30 U/L',
        }),
        defineMessage({
          id: 'quiz.table.blood.row61.name',
          defaultMessage: 'midcycle',
        }),
        defineMessage({
          id: 'quiz.table.blood.row61.range',
          defaultMessage: '75-150 mIU/mL',
        }),
        defineMessage({
          id: 'quiz.table.blood.row61.interval',
          defaultMessage: '75-150 U/L',
        }),
        defineMessage({
          id: 'quiz.table.blood.row62.name',
          defaultMessage: 'postmenopause',
        }),
        defineMessage({
          id: 'quiz.table.blood.row62.range',
          defaultMessage: '30-200 mIU/mL',
        }),
        defineMessage({
          id: 'quiz.table.blood.row62.interval',
          defaultMessage: '30-200 U/L',
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.blood.rowx41.name',
          defaultMessage: 'Osmolality, serum',
        }),
        defineMessage({
          id: 'quiz.table.blood.rowx41.range',
          defaultMessage: '275-295 mOsmol/kg H₂O',
        }),
        defineMessage({
          id: 'quiz.table.blood.rowx41.interval',
          defaultMessage: '275-295 mOsmol/kg H₂O',
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.blood.row63.name',
          defaultMessage: 'Parathyroid hormone, serum, N-terminal',
        }),
        defineMessage({
          id: 'quiz.table.blood.row63.range',
          defaultMessage: '10-65 pg/mL',
        }),
        defineMessage({
          id: 'quiz.table.blood.row63.interval',
          defaultMessage: '10-65 pg/mL',
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.blood.row64.name',
          defaultMessage: 'Phosphate (alkaline), serum (p-NPP at 30° C',
        }),
        defineMessage({
          id: 'quiz.table.blood.row64.range',
          defaultMessage: '20-70 U/L',
        }),
        defineMessage({
          id: 'quiz.table.blood.row64.interval',
          defaultMessage: '20-70 U/L',
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.blood.row65.name',
          defaultMessage: 'Phosphorus (inorganic), serum',
        }),
        defineMessage({
          id: 'quiz.table.blood.row65.range',
          defaultMessage: '3.0-4.5 mg/dL',
        }),
        defineMessage({
          id: 'quiz.table.blood.row65.interval',
          defaultMessage: '1.0-1.5 mmol/L',
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.blood.row66.name',
          defaultMessage: 'Prolactin, serum (hPRL)',
        }),
        defineMessage({
          id: 'quiz.table.blood.row66.range',
          defaultMessage: '20 ng/mL',
        }),
        defineMessage({
          id: 'quiz.table.blood.row66.interval',
          defaultMessage: '20 μg/L',
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.blood.row67.subheader',
          defaultMessage: 'Proteins, serum',
          isSubheader: true,
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.blood.row68.name',
          defaultMessage: 'Total (recumbent)',
        }),
        defineMessage({
          id: 'quiz.table.blood.row68.range',
          defaultMessage: '6.0-7.8 g/dL',
        }),
        defineMessage({
          id: 'quiz.table.blood.row68.interval',
          defaultMessage: '60-78 g/L',
        }),
        defineMessage({
          id: 'quiz.table.blood.row69.name',
          defaultMessage: 'Albumin',
        }),
        defineMessage({
          id: 'quiz.table.blood.row69.range',
          defaultMessage: '3.5-5.5 g/dL',
        }),
        defineMessage({
          id: 'quiz.table.blood.row69.interval',
          defaultMessage: '35-55 g/L',
        }),
        defineMessage({
          id: 'quiz.table.blood.row70.name',
          defaultMessage: 'Globulin',
        }),
        defineMessage({
          id: 'quiz.table.blood.row70.range',
          defaultMessage: '2.3-3.5 g/dL',
        }),
        defineMessage({
          id: 'quiz.table.blood.row70.interval',
          defaultMessage: '23-35 g/L',
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.blood.row71.name',
          defaultMessage: 'Thyroid-stimulating hormone (TSH), serum',
        }),
        defineMessage({
          id: 'quiz.table.blood.row71.range',
          defaultMessage: '0.5-5.0 μU/mL',
        }),
        defineMessage({
          id: 'quiz.table.blood.row71.interval',
          defaultMessage: '0.5-5.0 μU/mL',
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.blood.row72.name',
          defaultMessage: 'Thyroidal iodine (¹²³I) uptake',
        }),
        defineMessage({
          id: 'quiz.table.blood.row72.range',
          defaultMessage: '8%-30% of administered dose/24 h',
        }),
        defineMessage({
          id: 'quiz.table.blood.row72.interval',
          defaultMessage: '0.08-0.30 fraction of administered dose/24 h',
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.blood.row73.name',
          defaultMessage: 'Thyroxine (T₄), serum',
        }),
        defineMessage({
          id: 'quiz.table.blood.row73.range',
          defaultMessage: '5-12 μg/dL',
        }),
        defineMessage({
          id: 'quiz.table.blood.row73.interval',
          defaultMessage: '64-155 nmol/L',
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.blood.row74.name',
          defaultMessage: 'Triglycerides, serum',
        }),
        defineMessage({
          id: 'quiz.table.blood.row74.range',
          defaultMessage: '35-160 mg/dL',
        }),
        defineMessage({
          id: 'quiz.table.blood.row74.interval',
          defaultMessage: '0.4-1.81 mmol/L',
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.blood.row75.name',
          defaultMessage: 'Triiodothyronine (T₃), serum (RIA)',
        }),
        defineMessage({
          id: 'quiz.table.blood.row75.range',
          defaultMessage: '115-190 ng/dL',
        }),
        defineMessage({
          id: 'quiz.table.blood.row75.interval',
          defaultMessage: '	1.8-2.9 nmol/L',
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.blood.row76.name',
          defaultMessage: 'Triiodothyronine (T₃) resin uptake',
        }),
        defineMessage({
          id: 'quiz.table.blood.row76.range',
          defaultMessage: '25%-35%',
        }),
        defineMessage({
          id: 'quiz.table.blood.row76.interval',
          defaultMessage: '0.25-0.35',
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.blood.row77.name',
          defaultMessage: 'Urea nitrogen, serum (BUN)',
        }),
        defineMessage({
          id: 'quiz.table.blood.row77.range',
          defaultMessage: '7-18 mg/dL',
        }),
        defineMessage({
          id: 'quiz.table.blood.row77.interval',
          defaultMessage: '1.2-3.0 mmol/L',
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.blood.row78.name',
          defaultMessage: 'Uric acid, serum',
        }),
        defineMessage({
          id: 'quiz.table.blood.row78.range',
          defaultMessage: '3.0-8.2 mg/dL',
        }),
        defineMessage({
          id: 'quiz.table.blood.row78.interval',
          defaultMessage: '0.18-0.48 mmol/L',
        }),
      ],
    ],
  },
  hematologic: {
    headers: [
      defineMessage({
        id: 'quiz.table.hematologic.col1.header',
        defaultMessage: 'Hematologic',
      }),
      defineMessage({
        id: 'quiz.table.hematologic.col2.header',
        defaultMessage: 'Reference Range',
      }),
      defineMessage({
        id: 'quiz.table.hematologic.col3.header',
        defaultMessage: 'SI Reference Interval',
      }),
    ],
    rows: [
      [
        defineMessage({
          id: 'quiz.table.hematologic.row1.name',
          defaultMessage: 'Bleeding time (template)',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row1.range',
          defaultMessage: '2-7 minutes',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row1.interval',
          defaultMessage: '2-7 minutes',
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.hematologic.row2.name',
          defaultMessage: 'CD4+ T-lymphocyte count',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row2.range',
          defaultMessage: '>500 mm³',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row2.interval',
          defaultMessage: '>500 x 10⁶/L',
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.hematologic.row3.subheader',
          defaultMessage: 'Erythrocyte count',
          isSubheader: true,
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.hematologic.row4.name',
          defaultMessage: 'Male',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row4.range',
          defaultMessage: '4.3-5.9 million/mm³',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row4.interval',
          defaultMessage: '4.3-5.9 x 10¹²/L',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row5.name',
          defaultMessage: 'Female',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row5.range',
          defaultMessage: '3.5-5.5 million/mm³	',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row5.interval',
          defaultMessage: '3.5-5.5 x 10¹²/L',
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.hematologic.row6.subheader',
          defaultMessage: 'Erythrocyte sedimentation rate (Westergren)',
          isSubheader: true,
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.hematologic.row7.name',
          defaultMessage: 'Male',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row7.range',
          defaultMessage: '0-15 mm/h',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row7.interval',
          defaultMessage: '0-15 mm/h',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row8.name',
          defaultMessage: 'Female',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row8.range',
          defaultMessage: '0-20 mm/h',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row8.interval',
          defaultMessage: '0-20 mm/h',
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.hematologic.row9.subheader',
          defaultMessage: 'Hematocrit',
          isSubheader: true,
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.hematologic.row10.name',
          defaultMessage: 'Male',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row10.range',
          defaultMessage: '41%-53%',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row10.interval',
          defaultMessage: '0.41-0.53',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row11.name',
          defaultMessage: 'Female',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row11.range',
          defaultMessage: '36%-46%',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row11.interval',
          defaultMessage: '0.36-0.46',
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.hematologic.row12.name',
          defaultMessage: 'Hemoglobin A₁c',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row12.range',
          defaultMessage: '≤6%',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row12.interval',
          defaultMessage: '≤0.06%',
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.hematologic.row13.subheader',
          defaultMessage: 'Hemoglobin, blood',
          isSubheader: true,
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.hematologic.row14.name',
          defaultMessage: 'Male',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row14.range',
          defaultMessage: '13.5-17.5 g/dL',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row14.interval',
          defaultMessage: '2.09-2.71 mmol/L',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row15.name',
          defaultMessage: 'Female',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row15.range',
          defaultMessage: '12.0-16.0 g/dL',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row15.interval',
          defaultMessage: '1.86-2.48 mmol/L',
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.hematologic.row16.subheader',
          defaultMessage: 'Leukocyte count and differential',
          isSubheader: true,
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.hematologic.row17.name',
          defaultMessage: 'Leukocyte count',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row17.range',
          defaultMessage: '4,500-11,000/mm³',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row17.interval',
          defaultMessage: '4.5-11.0 x 10⁹/L',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row18.name',
          defaultMessage: 'Neutrophils, segmented',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row18.range',
          defaultMessage: '54%-62%',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row18.interval',
          defaultMessage: '0.54-0.62',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row19.name',
          defaultMessage: 'Neutrophils, banded',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row19.range',
          defaultMessage: '3%-5%',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row19.interval',
          defaultMessage: '0.03-0.05',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row20.name',
          defaultMessage: 'Eosinophils',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row20.range',
          defaultMessage: '1%-3%',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row20.interval',
          defaultMessage: '0.01-0.03',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row21.name',
          defaultMessage: 'Basophils',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row21.range',
          defaultMessage: '0%-0.75%',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row21.interval',
          defaultMessage: '0-0.0075',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row22.name',
          defaultMessage: 'Lymphocytes',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row22.range',
          defaultMessage: '25%-33%',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row22.interval',
          defaultMessage: '0.25-0.33',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row23.name',
          defaultMessage: 'Monocytes',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row23.range',
          defaultMessage: '3%-7%',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row23.interval',
          defaultMessage: '0.03-0.07',
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.hematologic.row24.name',
          defaultMessage: 'Mean corpuscular hemoglobin (MCH)',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row24.range',
          defaultMessage: '25.4-35.6 pg/cell',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row24.interval',
          defaultMessage: '0.39-0.54 fmol/cell',
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.hematologic.row25.name',
          defaultMessage: 'Mean corpuscular hemoglobin concentration (MCHC)',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row25.range',
          defaultMessage: '31%-36% Hb/cell',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row25.interval',
          defaultMessage: '4.81-5.58 mmol Hb/L',
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.hematologic.row26.name',
          defaultMessage: 'Mean corpuscular volume (MCV)',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row26.range',
          defaultMessage: '80-100 μm³',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row26.interval',
          defaultMessage: '80-100 fL',
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.hematologic.row27.name',
          defaultMessage: 'Partial thromboplastin time (activated)',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row27.range',
          defaultMessage: '25-40 seconds',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row27.interval',
          defaultMessage: '25-40 seconds',
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.hematologic.row28.name',
          defaultMessage: 'Platelet count',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row28.range',
          defaultMessage: '150,000-400,000/mm³',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row28.interval',
          defaultMessage: '150-400 x 10⁹/L',
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.hematologic.row29.name',
          defaultMessage: 'Prothombin time',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row29.range',
          defaultMessage: '11-15 seconds',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row29.interval',
          defaultMessage: '11-15 seconds',
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.hematologic.row30.name',
          defaultMessage: 'Reticulocyte count',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row30.range',
          defaultMessage: '0.5%-1.5% of red cells',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row30.interval',
          defaultMessage: '0.005-0.015 fraction of red cells',
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.hematologic.row31.name',
          defaultMessage: 'Thrombin time',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row31.range',
          defaultMessage: '<2 seconds deviation from control',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row31.interval',
          defaultMessage: '<2 seconds deviation from control',
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.hematologic.row32.subheader',
          defaultMessage: 'Volume',
          isSubheader: true,
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.hematologic.row33.name',
          defaultMessage: 'Plasma',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row33.range',
          defaultMessage: ' ',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row33.interval',
          defaultMessage: ' ',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row34.name',
          defaultMessage: 'Male',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row34.range',
          defaultMessage: '25-43 mL/kg',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row34.interval',
          defaultMessage: '0.025-0.043 L/kg',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row36.name',
          defaultMessage: 'Female',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row36.range',
          defaultMessage: '28-45 mL/kg',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row36.interval',
          defaultMessage: '0.028-0.045 L/kg',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row36.name.cell',
          defaultMessage: 'Red cell',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row36.range.space',
          defaultMessage: ' ',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row36.interval_space',
          defaultMessage: ' ',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row37.name',
          defaultMessage: 'Male',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row37.range',
          defaultMessage: '20-36 mL/kg',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row37.interval',
          defaultMessage: '0.020-0.036 L/kg',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row37.name.female',
          defaultMessage: 'Female',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row37.range.19',
          defaultMessage: '19-31 mL/kg',
        }),
        defineMessage({
          id: 'quiz.table.hematologic.row37.interval.31',
          defaultMessage: '0.019-0.031 L/kg',
        }),
      ],
    ],
  },
  cerebrospinal: {
    headers: [
      defineMessage({
        id: 'quiz.table.cerebrospinal.col1.header',
        defaultMessage: 'Cerebrospinal Fluid',
      }),
      defineMessage({
        id: 'quiz.table.cerebrospinal.col2.header',
        defaultMessage: 'Reference Range',
      }),
      defineMessage({
        id: 'quiz.table.cerebrospinal.col3.header',
        defaultMessage: 'SI Reference Interval',
      }),
    ],
    rows: [
      [
        defineMessage({
          id: 'quiz.table.cerebrospinal.row1.name',
          defaultMessage: 'Cell count',
        }),
        defineMessage({
          id: 'quiz.table.cerebrospinal.row1.range',
          defaultMessage: '0-5/mm³',
        }),
        defineMessage({
          id: 'quiz.table.cerebrospinal.row1.interval',
          defaultMessage: '0-5 x 10⁶/L',
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.cerebrospinal.row2.name',
          defaultMessage: 'Chloride',
        }),
        defineMessage({
          id: 'quiz.table.cerebrospinal.row2.range',
          defaultMessage: '118-132 mEq/L',
        }),
        defineMessage({
          id: 'quiz.table.cerebrospinal.row2.interval',
          defaultMessage: '118-132 mmol/L',
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.cerebrospinal.row3.name',
          defaultMessage: 'Gamma globulin',
        }),
        defineMessage({
          id: 'quiz.table.cerebrospinal.row3.range',
          defaultMessage: '3%-12% of total proteins',
        }),
        defineMessage({
          id: 'quiz.table.cerebrospinal.row3.interval',
          defaultMessage: '0.03-0.12 of total proteins',
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.cerebrospinal.row4.name',
          defaultMessage: 'Glucose',
        }),
        defineMessage({
          id: 'quiz.table.cerebrospinal.row4.range',
          defaultMessage: '40-70 mg/dL',
        }),
        defineMessage({
          id: 'quiz.table.cerebrospinal.row4.interval',
          defaultMessage: '2.2-3.9 mmol/L',
        }),
        defineMessage({
          id: 'quiz.table.cerebrospinal.row5.name',
          defaultMessage: 'Pressure',
        }),
        defineMessage({
          id: 'quiz.table.cerebrospinal.row5.range',
          defaultMessage: '70-180 mm H₂O	',
        }),
        defineMessage({
          id: 'quiz.table.cerebrospinal.row5.interval',
          defaultMessage: '70-180 mm H₂O',
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.cerebrospinal.row6.name',
          defaultMessage: 'Proteins, total',
        }),
        defineMessage({
          id: 'quiz.table.cerebrospinal.row6.range',
          defaultMessage: '<40 mg/dL',
        }),
        defineMessage({
          id: 'quiz.table.cerebrospinal.row6.interval',
          defaultMessage: '<0.40 g/L',
        }),
      ],
    ],
  },
  sweat: {
    headers: [
      defineMessage({
        id: 'quiz.table.sweat.col1.header',
        defaultMessage: 'Sweat',
      }),
      defineMessage({
        id: 'quiz.table.sweat.col2.header',
        defaultMessage: 'Reference Range',
      }),
      defineMessage({
        id: 'quiz.table.sweat.col3.header',
        defaultMessage: 'SI Reference Interval',
      }),
    ],
    rows: [
      [
        defineMessage({
          id: 'quiz.table.sweat.row1.name',
          defaultMessage: 'Chloride',
        }),
        defineMessage({
          id: 'quiz.table.sweat.row1.range',
          defaultMessage: '0-35 mmol/L',
        }),
        defineMessage({
          id: 'quiz.table.sweat.row1.interval',
          defaultMessage: '0-35 mmol/L',
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.sweat.row2.name',
          defaultMessage: 'Urine',
        }),
        defineMessage({
          id: 'quiz.table.sweat.row2.range',
          defaultMessage: 'Reference Range',
        }),
        defineMessage({
          id: 'quiz.table.sweat.row2.interval',
          defaultMessage: 'SI Reference Intervals',
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.sweat.row4.name',
          defaultMessage: 'Calcium',
        }),
        defineMessage({
          id: 'quiz.table.sweat.row4.range',
          defaultMessage: '100-300 mg/24 h',
        }),
        defineMessage({
          id: 'quiz.table.sweat.row4.interval',
          defaultMessage: '2.5-7.5 mmol/24 h',
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.sweat.row5.sweatheader',
          defaultMessage: 'Creatine clearance',
          issweatheader: true,
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.sweat.row6.name',
          defaultMessage: 'Male',
        }),
        defineMessage({
          id: 'quiz.table.sweat.row6.range',
          defaultMessage: '97-137 mL/min	',
        }),
        defineMessage({
          id: 'quiz.table.sweat.row6.interval',
          defaultMessage: '1.62-2.29 mL/s',
        }),
        defineMessage({
          id: 'quiz.table.sweat.row7.name',
          defaultMessage: 'Female',
        }),
        defineMessage({
          id: 'quiz.table.sweat.row7.range',
          defaultMessage: '88-128 mL/min',
        }),
        defineMessage({
          id: 'quiz.table.sweat.row7.interval',
          defaultMessage: '1.47-2.14 mL/s',
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.sweat.row8.sweatheader',
          defaultMessage: 'Estriol, total (in pregnancy)',
          issweatheader: true,
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.sweat.row9.name',
          defaultMessage: '30 wks',
        }),
        defineMessage({
          id: 'quiz.table.sweat.row9.range',
          defaultMessage: '6-18 mg/24 h',
        }),
        defineMessage({
          id: 'quiz.table.sweat.row9.interval',
          defaultMessage: '21-62 μmol/24 h',
        }),
        defineMessage({
          id: 'quiz.table.sweat.row10.name',
          defaultMessage: '35 wks',
        }),
        defineMessage({
          id: 'quiz.table.sweat.row10.range',
          defaultMessage: '9-28 mg/24 h',
        }),
        defineMessage({
          id: 'quiz.table.sweat.row10.interval',
          defaultMessage: '31-97 μmol/24 h',
        }),
        defineMessage({
          id: 'quiz.table.sweat.row11.name',
          defaultMessage: '40 wks',
        }),
        defineMessage({
          id: 'quiz.table.sweat.row11.range',
          defaultMessage: '13-42 mg/24 h',
        }),
        defineMessage({
          id: 'quiz.table.sweat.row11.interval',
          defaultMessage: '45-146 μmol/24 h',
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.sweat.row12.sweatheader',
          defaultMessage: '17-hydroxycoticosteroids',
          issweatheader: true,
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.sweat.row13.name',
          defaultMessage: 'Male',
        }),
        defineMessage({
          id: 'quiz.table.sweat.row13.range',
          defaultMessage: '3.0-10.0 mg/24 h',
        }),
        defineMessage({
          id: 'quiz.table.sweat.row13.interval',
          defaultMessage: '8.2-27.6 μmol/24 h',
        }),

        defineMessage({
          id: 'quiz.table.sweat.row14.name',
          defaultMessage: 'Female',
        }),
        defineMessage({
          id: 'quiz.table.sweat.row14.range',
          defaultMessage: '2.0-8.0 mg/24 h',
        }),
        defineMessage({
          id: 'quiz.table.sweat.row14.interval',
          defaultMessage: '5.5-22.0 μmol/24 h',
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.sweat.row15.sweatheader',
          defaultMessage: '17-ketosteroids, total',
          issweatheader: true,
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.sweat.16.name',
          defaultMessage: 'Male',
        }),
        defineMessage({
          id: 'quiz.table.sweat.row16.range',
          defaultMessage: '8-20 mg/24 h',
        }),
        defineMessage({
          id: 'quiz.table.sweat.row16.interval',
          defaultMessage: '28-70 μmol/24 h',
        }),
        defineMessage({
          id: 'quiz.table.sweat.row17.name',
          defaultMessage: 'Female',
        }),
        defineMessage({
          id: 'quiz.table.sweat.row17.range',
          defaultMessage: '6-15 mg/24 h',
        }),
        defineMessage({
          id: 'quiz.table.sweat.row17.interval',
          defaultMessage: '21-52 μmol/24 h',
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.sweat.row18.name',
          defaultMessage: 'Osmolality',
        }),
        defineMessage({
          id: 'quiz.table.sweat.row18.range',
          defaultMessage: '50-1,400 mOsmol/kg H₂O',
        }),
        defineMessage({
          id: 'quiz.table.sweat.row18.interval',
          defaultMessage: '50-1,400 mmol/kg',
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.sweat.row19.name',
          defaultMessage: 'Oxalate',
        }),
        defineMessage({
          id: 'quiz.table.sweat.row19.range',
          defaultMessage: '8-40 μg/mL',
        }),
        defineMessage({
          id: 'quiz.table.sweat.row19.interval',
          defaultMessage: '90-445 μmol/L',
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.sweat.row20.name',
          defaultMessage: 'Proteins, total',
        }),
        defineMessage({
          id: 'quiz.table.sweat.row20.range',
          defaultMessage: '<150 mg/24 h',
        }),
        defineMessage({
          id: 'quiz.table.sweat.row20.interval',
          defaultMessage: '< 0.15 g/24 h',
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.sweat.row21.name',
          defaultMessage: 'Sodium, total',
        }),
        defineMessage({
          id: 'quiz.table.sweat.row21.range',
          defaultMessage: 'varies with diet',
        }),
        defineMessage({
          id: 'quiz.table.sweat.row21.interval',
          defaultMessage: 'varies with diet',
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.sweat.row22.name',
          defaultMessage: 'Uric acid',
        }),
        defineMessage({
          id: 'quiz.table.sweat.row22.range',
          defaultMessage: 'varies with diet',
        }),
        defineMessage({
          id: 'quiz.table.sweat.row22.interval',
          defaultMessage: 'varies with diet',
        }),
      ],
      [
        defineMessage({
          id: 'quiz.table.sweat.row23.name',
          defaultMessage: 'Body Mass Index (Adult)',
        }),
        defineMessage({
          id: 'quiz.table.sweat.row23.range',
          defaultMessage: '19-25 kg/m²',
        }),
        defineMessage({
          id: 'quiz.table.sweat.row23.interval',
          defaultMessage: '19-25 kg/m²',
        }),
      ],
    ],
  },
};

/**
 * QuizRepetitionSettingsValues.
 */
export const quizRepetitionSettingsValues = (intl: IntlShape) => [
  {
    value: 'osmosis',
    label: intl.formatMessage({
      defaultMessage: 'Auto',
      id: 'repetitionSettingsField.select.osmosis',
    }),
  },
  {
    value: '1m',
    label: intl.formatMessage({
      defaultMessage: '1 minute',
      id: 'repetitionSettingsField.select.1minute',
    }),
  },
  {
    value: '5m',
    label: intl.formatMessage({
      defaultMessage: '5 minutes',
      id: 'repetitionSettingsField.select.5minutes',
    }),
  },
  {
    value: '10m',
    label: intl.formatMessage({
      defaultMessage: '10 minutes',
      id: 'repetitionSettingsField.select.10minutes',
    }),
  },
  {
    value: '1h',
    label: intl.formatMessage({
      defaultMessage: '1 hour',
      id: 'repetitionSettingsField.select.1hour',
    }),
  },
  {
    value: '1d',
    label: intl.formatMessage({
      defaultMessage: '1 day',
      id: 'repetitionSettingsField.select.1day',
    }),
  },
];

export const dailyPracticeMessages = defineMessages({
  correctWordCorrect: {
    id: 'dailyPractice.correctWord-Correct',
    defaultMessage: 'Correct!',
  },
  correctWordNice: {
    id: 'dailyPractice.correctWord-Nice',
    defaultMessage: 'Nice!',
  },
  correctWordAwesome: {
    id: 'dailyPractice.correctWord-Awesome',
    defaultMessage: 'Awesome!',
  },
  correctWordExcellent: {
    id: 'dailyPractice.correctWord-Excellent',
    defaultMessage: 'Excellent!',
  },
  correctWordGreat: {
    id: 'dailyPractice.correctWord-Great',
    defaultMessage: 'Great!',
  },
  correctWordNiceJob: {
    id: 'dailyPractice.correctWord-NiceJob',
    defaultMessage: 'Nice job',
  },
  correctWordNiceWork: {
    id: 'dailyPractice.correctWord-NiceWork',
    defaultMessage: 'Nice work',
  },
  correctWordWoot: {
    id: 'dailyPractice.correctWord-Woot',
    defaultMessage: 'Woot!',
  },
  incorrectWordShoot: {
    id: 'dailyPractice.incorrectWord-Shoot',
    defaultMessage: 'Shoot',
  },
  incorrectWordDang: {
    id: 'dailyPractice.incorrectWord-Dang',
    defaultMessage: 'Dang',
  },
  incorrectWordDarn: {
    id: 'dailyPractice.incorrectWord-Darn',
    defaultMessage: 'Darn',
  },
  incorrectWordWhoops: {
    id: 'dailyPractice.incorrectWord-Whoops',
    defaultMessage: 'Whoops',
  },
  incorrectWordMissedIt: {
    id: 'dailyPractice.incorrectWord-MissedIt',
    defaultMessage: 'Missed it',
  },
});

export const SPACED_REPETITION_LINK =
  'https://www.elsevier.support/osmosis/answer/how-does-my-daily-spaced-repetition-quiz-work';

export const QUIZ_CONFIDENCE_POPUP_KEY = 'isQuizConfidencePopupHidden';
